// src/pages/manager/batches/BatchForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../api";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../../components/common/Button";
import { TextInput, SelectInput, RadioGroup, DayToggleGroup } from "../../../components/common/form";
import PageHeaderSection from "../../../components/layout/PageHeaderSection";
import PageLoadingShell from "../../../components/layout/PageLoadingShell";
import { TimePicker15Min } from "../../../components/common/TimePicker15Min";
import { useRegionalSettings } from "../../../context/RegionalSettingsContext";
import { formatSessionDays, normalizeSessionDaysString, parseSessionDays } from "../../../utils/sessionDays";
import { formatLevelLabel } from "../../../utils/formatLevelLabel";
import { getCarryForwardModeOptions } from "../../../utils/carryForwardCopy";
import { BATCH_LOCK_NOTES, buildBatchUpdateBody, canEditBatchField } from "../../../utils/batchEditRules";
import { coachDisplayName } from '../../../utils/displayNames';
import {
    CHARGES_MODE_LABELS,
    formatChargesMode,
    getPeriodIntervalOptions,
} from "../../../utils/periodBillingCopy";
import { apiErrorMessage } from '../../../utils/apiError';

const MODE = ["ANY", "ONLINE", "OFFLINE"];
const CHARGES_MODE = ["PER_SESSION", "PER_SESSION_HOURLY", "PER_PERIOD"];

const empty = {
    branchId: "",
    levelId: "",
    coachId: "",
    name: "",
    sessionMode: "ANY",
    sessionDays: "",
    defaultStartTod: "",
    defaultEndTod: "",
    chargesMode: "PER_SESSION_HOURLY",
    sessionCharge: "",
    periodInterval: "MONTHLY",
    periodCharge: "",
    carryForwardMode: "NOT_ALLOWED",
};

function normalizeToString(v) {
    if (v === null || v === undefined) return "";
    return String(v);
}

function resolveIdFromPayload(d, idKeys = ["branchId", "branch_id"], nestedKey = "branch", nestedIdKeys = ["id", "branchId"]) {
    for (const k of idKeys) {
        if (d[k] != null) return String(d[k]);
    }
    if (d[nestedKey]) {
        for (const nk of nestedIdKeys) {
            if (d[nestedKey][nk] != null) return String(d[nestedKey][nk]);
        }
    }
    return "";
}

export default function ManagerBatchFormPage() {
    const { t } = useTranslation();
    const { settings } = useRegionalSettings();
    const currencyLabel = settings?.currencySymbol || settings?.currencyCode || '';
    const { id } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(empty);
    const [levels, setLevels] = useState([]);
    const [coaches, setCoaches] = useState([]);
    const [branches, setBranches] = useState([]);
    const [saving, setSaving] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [hasActivity, setHasActivity] = useState(false);
    const [systemManaged, setSystemManaged] = useState(false);
    const [optionsLoadError, setOptionsLoadError] = useState(false);
    const [loadError, setLoadError] = useState("");

    const isEdit = Boolean(id);
    const locks = { isEdit, hasActivity, systemManaged };

    useEffect(() => {
        let mounted = true;

        (async () => {
            try {
            let branchesResp = null;
            try {
                const lv = await API.get("/manager/levels");
                if (!mounted) return;
                setLevels(lv?.data?.content ?? lv?.data ?? []);
            } catch (e) {
                console.error("Failed to load levels", e);
                if (!mounted) return;
                setLevels([]);
                setOptionsLoadError(true);
            }

            try {
                const u = await API.get("/manager/users", { params: { role: "COACH", active: true, page: 0, size: 200 } });
                if (!mounted) return;
                const list = u?.data?.content ?? u?.data ?? [];
                setCoaches(list);
            } catch (e) {
                console.error("Failed to load coaches", e);
                if (!mounted) return;
                setCoaches([]);
                setOptionsLoadError(true);
            }

            try {
                branchesResp = await API.get("/manager/branches/mine");
                if (!mounted) return;
                const br = branchesResp?.data?.content ?? branchesResp?.data ?? [];
                setBranches(br);
            } catch (e) {
                console.error("Failed to load branches", e);
                if (!mounted) return;
                setBranches([]);
                setOptionsLoadError(true);
            }

            if (id) {
                let batchLoaded = false;
                setLoadError("");
                try {
                    const res = await API.get(`/manager/batches/${id}`);
                    const d = res?.data;
                    if (d) {
                        let resolvedBranchId = resolveIdFromPayload(d, ["branchId", "branch_id"], "branch", ["id", "branchId"]);
                        const resolvedLevelId = normalizeToString(d.levelId ?? d.level_id ?? (d.level && (d.level.levelId ?? d.level.id)) ?? "");
                        const resolvedCoachId = normalizeToString(d.coachId ?? d.coach_id ?? (d.coach && (d.coach.userId ?? d.coach.id)) ?? "");

                        if (!mounted) return;
                        setForm({
                            branchId: resolvedBranchId || "",
                            levelId: resolvedLevelId,
                            coachId: resolvedCoachId,
                            name: d.name ?? "",
                            sessionMode: d.sessionMode ?? d.session_mode ?? "ANY",
                            sessionDays: normalizeSessionDaysString(d.sessionDays ?? d.session_days ?? ""),
                            defaultStartTod: d.defaultStartTod ?? d.default_start_tod ?? "",
                            defaultEndTod: d.defaultEndTod ?? d.default_end_tod ?? "",
                            chargesMode: d.chargesMode ?? d.charges_mode ?? "PER_SESSION_HOURLY",
                            periodInterval: d.periodInterval ?? d.period_interval ?? "MONTHLY",
                            periodCharge: d.periodCharge ?? d.period_charge ?? "",
                            sessionCharge: d.sessionCharge ?? d.session_charge ?? "",
                            carryForwardMode: d.carryForwardMode ?? d.carry_forward_mode ?? "NOT_ALLOWED",
                        });
                        setHasActivity(!!d.hasActivity);
                        setSystemManaged(!!d.systemManaged);
                        batchLoaded = true;
                    }
                } catch (e) {
                    console.warn("Direct GET failed - falling back to list: ", e);
                    try {
                        const listRes = await API.get("/manager/batches", { params: { page: 0, size: 500 } });
                        const items = listRes?.data?.content ?? listRes?.data ?? [];
                        const found = (items || []).find(
                            (it) => String(it.batchId ?? it.id ?? it.batch_id) === String(id)
                        );
                        if (found) {
                            const d = found;
                            let resolvedBranchId = resolveIdFromPayload(d, ["branchId", "branch_id"], "branch", ["id", "branchId"]);
                            const resolvedLevelId = normalizeToString(d.levelId ?? d.level_id ?? (d.level && (d.level.levelId ?? d.level.id)) ?? "");
                            const resolvedCoachId = normalizeToString(d.coachId ?? d.coach_id ?? (d.coach && (d.coach.userId ?? d.coach.id)) ?? "");

                            if (!mounted) return;
                            setForm({
                                branchId: resolvedBranchId || "",
                                levelId: resolvedLevelId,
                                coachId: resolvedCoachId,
                                name: d.name ?? "",
                                sessionMode: d.sessionMode ?? d.session_mode ?? "ANY",
                                sessionDays: normalizeSessionDaysString(d.sessionDays ?? d.session_days ?? ""),
                                defaultStartTod: d.defaultStartTod ?? d.default_start_tod ?? "",
                                defaultEndTod: d.defaultEndTod ?? d.default_end_tod ?? "",
                                chargesMode: d.chargesMode ?? d.charges_mode ?? "PER_SESSION_HOURLY",
                            periodInterval: d.periodInterval ?? d.period_interval ?? "MONTHLY",
                            periodCharge: d.periodCharge ?? d.period_charge ?? "",
                                sessionCharge: d.sessionCharge ?? d.session_charge ?? "",
                                carryForwardMode: d.carryForwardMode ?? d.carry_forward_mode ?? "NOT_ALLOWED",
                            });
                            setHasActivity(!!d.hasActivity);
                            setSystemManaged(!!d.systemManaged);
                            batchLoaded = true;
                        }
                    } catch (err) {
                        console.error("Failed to load batch via list fallback", err);
                    }
                }
                if (!batchLoaded && mounted) {
                    setLoadError(t('forms.batch.loadFailed', { defaultValue: 'Could not load this batch.' }));
                }
            }
        } finally {
                if (mounted) setInitialLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [id, t]);

    const branchLevels = useMemo(() => {
        if (!form.branchId) return [];
        return levels.filter((l) => {
            const bid = l.branchId ?? l.branch_id ?? l.branch?.id ?? l.branch?.branchId;
            return bid != null && String(bid) === String(form.branchId);
        });
    }, [levels, form.branchId]);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => {
            const next = { ...f, [name]: type === "checkbox" ? checked : value };
            if (name === "branchId") {
                next.levelId = "";
            }
            if (name === "chargesMode" && value === "PER_PERIOD") {
                next.carryForwardMode = "NOT_ALLOWED";
            }
            return next;
        });
    };

    const toggleDay = (v) => {
        const set = new Set(parseSessionDays(form.sessionDays));
        if (set.has(v)) set.delete(v);
        else set.add(v);
        setForm((f) => ({ ...f, sessionDays: formatSessionDays([...set]) }));
    };

    const validate = () => {
        const errs = [];
        if (!form.branchId) errs.push(t('forms.batch.validation.selectBranch'));
        if (!form.levelId) errs.push(t('forms.batch.validation.selectLevel'));
        if (!form.coachId) errs.push(t('forms.batch.validation.selectCoach'));
        if (!form.sessionMode) errs.push(t('forms.batch.validation.selectSessionMode'));
        if (!form.name || String(form.name).trim() === "") errs.push(t('forms.batch.validation.enterName'));
        const selectedDays = new Set(parseSessionDays(form.sessionDays));
        if (selectedDays.size === 0) errs.push(t('forms.batch.validation.selectSessionDay'));
        if (!form.defaultStartTod) errs.push(t('forms.batch.validation.selectDefaultStart'));
        if (!form.defaultEndTod) errs.push(t('forms.batch.validation.selectDefaultEnd'));
        if (form.defaultStartTod && form.defaultEndTod && form.defaultEndTod <= form.defaultStartTod) {
            errs.push(t('forms.batch.validation.endAfterStart'));
        }
        return errs;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        const errs = validate();
        if (errs.length) {
            alert(errs.join("\n"));
            setSaving(false);
            return;
        }

        try {
            const body = buildBatchUpdateBody(
                {
                    ...form,
                    sessionDays: formatSessionDays(parseSessionDays(form.sessionDays)),
                },
                { isEdit, hasActivity }
            );
            if (id) {
                await API.put(`/manager/batches/${id}`, body);
            } else {
                await API.post(`/manager/batches`, body);
            }
            navigate("/manager/batches");
        } catch (err) {
            console.error(err);
            const msg = apiErrorMessage(err, t('forms.batch.validation.saveFailed'));
            alert(msg);
        } finally {
            setSaving(false);
        }
    };

    const selected = new Set(parseSessionDays(form.sessionDays));

    const batchTitle = id ? t('managerForms.editBatch') : t('managerForms.createBatch');
    const batchSubtitle = t('managerForms.batchSubtitle');

    if (initialLoading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={batchTitle}
                subtitle={batchSubtitle}
            />
        );
    }

    if (isEdit && loadError) {
        return (
            <div className="page-shell-form">
                <PageHeaderSection
                    title={batchTitle}
                    subtitle={batchSubtitle}
                />
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    {loadError}
                </div>
                <div className="mt-4">
                    <Button type="button" variant="secondary" onClick={() => navigate('/manager/batches')}>
                        {t('common.back', { defaultValue: 'Back' })}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                title={batchTitle}
                subtitle={batchSubtitle}
            />

            {systemManaged && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    {BATCH_LOCK_NOTES.systemManaged}
                </div>
            )}
            {optionsLoadError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {t('common.optionsLoadFailed')}
                </div>
            )}

            <form onSubmit={onSubmit} className="section-card grid grid-cols-1 md:grid-cols-3 gap-4">
                <SelectInput
                    label={t('forms.batch.branch')}
                    name="branchId"
                    value={form.branchId}
                    onChange={onChange}
                    required
                    disabled={optionsLoadError || !canEditBatchField('branch', locks)}
                    note={isEdit ? BATCH_LOCK_NOTES.branch : undefined}
                    className={isEdit ? 'disabled:opacity-50 disabled:cursor-not-allowed' : ''}
                >
                    <option value="">{t('forms.batch.selectEllipsis')}</option>
                    {branches.map((b) => (
                        <option key={b.id ?? b.branchId} value={b.id ?? b.branchId}>
                            {b.name || b.branchName}
                        </option>
                    ))}
                </SelectInput>

                <SelectInput
                    label={t('forms.batch.level')}
                    name="levelId"
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    value={form.levelId}
                    onChange={onChange}
                    required
                    disabled={!form.branchId || !canEditBatchField('level', locks)}
                    note={isEdit ? BATCH_LOCK_NOTES.level : undefined}
                >
                    <option value="">{form.branchId ? t('forms.batch.selectEllipsis') : t('forms.batch.selectBranchFirst')}</option>
                    {branchLevels.map((l) => (
                        <option key={l.id ?? l.levelId} value={l.id ?? l.levelId}>
                            {formatLevelLabel(l)}
                        </option>
                    ))}
                </SelectInput>

                <SelectInput
                    label={t('forms.batch.coach')}
                    name="coachId"
                    value={form.coachId}
                    onChange={onChange}
                    required
                    disabled={!canEditBatchField('coach', locks)}
                >
                    <option value="">{t('forms.batch.selectEllipsis')}</option>
                    {coaches.map((c) => {
                        const nameParts = [c.firstName, c.middleName, c.lastName].filter(Boolean);
                        const fullName = nameParts.length > 0 ? nameParts.join(" ") : c.username || coachDisplayName(null, c.id ?? c.userId, t);
                        return (
                            <option key={c.id ?? c.userId} value={c.id ?? c.userId}>
                                {fullName}
                            </option>
                        );
                    })}
                </SelectInput>

                <TextInput
                    label={t('forms.batch.name')}
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    disabled={!canEditBatchField('name', locks)}
                />

                <SelectInput
                    label={t('forms.batch.sessionMode')}
                    name="sessionMode"
                    value={form.sessionMode}
                    onChange={onChange}
                    required
                    disabled={!canEditBatchField('sessionMode', locks)}
                >
                    {MODE.map((m) => (
                        <option key={m} value={m}>
                            {t(`sessionModes.${m}`, { defaultValue: m })}
                        </option>
                    ))}
                </SelectInput>

                <DayToggleGroup
                    selected={selected}
                    onToggle={toggleDay}
                    required
                    disabled={!canEditBatchField('schedule', locks)}
                />

                <div>
                    <TimePicker15Min
                        value={form.defaultStartTod}
                        onChange={(val) => setForm(f => ({
                            ...f,
                            defaultStartTod: val,
                            defaultEndTod: f.defaultEndTod && val && f.defaultEndTod <= val ? "" : f.defaultEndTod,
                        }))}
                        placeholder={t('forms.batch.selectStartTime')}
                        label={t('forms.batch.defaultStart')}
                        className="w-full"
                        required
                        showDuration={true}
                        otherTime={form.defaultEndTod}
                        maxTime={form.defaultEndTod || null}
                        disabled={!canEditBatchField('schedule', locks)}
                    />
                </div>

                <div>
                    <TimePicker15Min
                        value={form.defaultEndTod}
                        onChange={(val) => setForm(f => ({ ...f, defaultEndTod: val }))}
                        placeholder={t('forms.batch.selectEndTime')}
                        label={t('forms.batch.defaultEnd')}
                        className="w-full"
                        required
                        showDuration={true}
                        otherTime={form.defaultStartTod}
                        minTime={form.defaultStartTod || null}
                        disabled={!canEditBatchField('schedule', locks)}
                    />
                </div>

                <div className="md:col-span-3 border-t pt-4">
                    <h3 className="form-section-title">{t('forms.batch.billingConfig')}</h3>
                </div>

                <SelectInput
                    label={t('forms.batch.chargesMode')}
                    name="chargesMode"
                    value={form.chargesMode}
                    onChange={onChange}
                    required
                    disabled={!canEditBatchField('chargesMode', locks)}
                    note={
                        isEdit && hasActivity
                            ? BATCH_LOCK_NOTES.chargesMode
                            : form.chargesMode === "PER_SESSION"
                              ? t('forms.batch.notePerSession')
                            : form.chargesMode === "PER_SESSION_HOURLY"
                                ? t('forms.batch.notePerSessionHourly')
                                : form.chargesMode === "PER_PERIOD"
                                  ? t('forms.batch.notePerPeriod')
                                  : undefined
                    }
                >
                    {CHARGES_MODE.map((m) => (
                        <option key={m} value={m}>
                            {formatChargesMode(m)}
                        </option>
                    ))}
                </SelectInput>

                {(form.chargesMode === "PER_SESSION" || form.chargesMode === "PER_SESSION_HOURLY") && (
                    <TextInput
                        label={t('forms.batch.sessionCharge', { currency: currencyLabel })}
                        type="number"
                        name="sessionCharge"
                        value={form.sessionCharge}
                        onChange={onChange}
                        step="0.01"
                        min="0"
                        required
                        disabled={!canEditBatchField('sessionCharge', locks)}
                        note={isEdit ? BATCH_LOCK_NOTES.sessionCharge : undefined}
                    />
                )}

                {form.chargesMode === "PER_PERIOD" && (
                    <>
                        <SelectInput
                            label={t('forms.batch.periodLength')}
                            name="periodInterval"
                            value={form.periodInterval}
                            onChange={onChange}
                            required
                            disabled={!canEditBatchField('chargesMode', locks)}
                        >
                            {getPeriodIntervalOptions().map((p) => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </SelectInput>
                        <TextInput
                            label={t('forms.batch.periodFee', { currency: currencyLabel })}
                            type="number"
                            name="periodCharge"
                            value={form.periodCharge}
                            onChange={onChange}
                            step="0.01"
                            min="0"
                            required
                            disabled={!canEditBatchField('periodCharge', locks)}
                            note={!canEditBatchField('periodCharge', locks) ? BATCH_LOCK_NOTES.monthlyYearly : undefined}
                        />
                    </>
                )}

                {form.chargesMode !== "PER_PERIOD" && (
                <RadioGroup
                    className="md:col-span-3"
                    label={t('forms.batch.carryForward')}
                    name="carryForwardMode"
                    value={form.carryForwardMode}
                    onChange={onChange}
                    options={getCarryForwardModeOptions()}
                    required
                    disabled={!canEditBatchField('carryForward', locks)}
                    note={!canEditBatchField('carryForward', locks) ? BATCH_LOCK_NOTES.carryForward : undefined}
                />
                )}

                {form.chargesMode === "PER_PERIOD" && (
                    <p className="md:col-span-3 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                        {t('forms.batch.periodNoCarryForward')}
                    </p>
                )}

                <div className="md:col-span-3 flex items-center justify-end gap-2 mt-2">
                    <Button type="button" variant="secondary" onClick={() => navigate('/manager/batches')}>
                        {t('common.cancel')}
                    </Button>
                    <Button type="submit" variant="primary" disabled={saving || systemManaged}>
                        {saving ? t('forms.common.saving') : t('forms.common.save')}
                    </Button>
                </div>
            </form>
        </div>
    );
}